import {useDocumentTitle} from "usehooks-ts";
import BackButton from "@/components/BackButton.tsx";

export function PrivacyPolicy(){
    useDocumentTitle("Privacy Policy")
    return <div className={"container mx-auto mt-16 prose"} style={{maxWidth:600}}>
        <BackButton />
        <h1>Privacy Policy</h1>

        <p><strong>Effective Date:</strong> 28 Dec 2024</p>

        <p>TimelyTurtle ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how
            we collect, use, disclose, and safeguard your information when you use our social media post scheduling app
            (the "Service"). Please read this Privacy Policy carefully. By using TimelyTurtle, you agree to the terms
            outlined below.</p>

        <hr/>

        <h2>1. Information We Collect</h2>

        <h3>a. Information You Provide</h3>
        <ul>
            <li><strong>Account Information:</strong> When you sign up, we collect your name, email address, and
                password.
            </li>
            <li><strong>Social Media Credentials:</strong> To schedule posts, you may provide access to your social
                media accounts through third-party APIs.
            </li>
            <li><strong>Content:</strong> The posts, images, videos, and other content you schedule through the Service.
            </li>
        </ul>

        <h3>b. Automatically Collected Information</h3>
        <ul>
            <li><strong>Device Information:</strong> IP address, device type, operating system, and browser type.</li>
            <li><strong>Usage Data:</strong> Pages visited, features used, and actions taken within the app.</li>
        </ul>

        <h3>c. Information from Third Parties</h3>
        <ul>
            <li><strong>Social Media Platforms:</strong> We may collect data such as profile information, follower
                counts, and engagement metrics, as authorized by you when linking your social accounts.
            </li>
        </ul>

        <hr/>

        <h2>2. How We Use Your Information</h2>
        <ul>
            <li>Provide and improve the Service.</li>
            <li>Schedule and publish posts to your linked social media accounts.</li>
            <li>Analyze app usage to enhance functionality and user experience.</li>
            <li>Communicate with you about updates, promotions, and support.</li>
            <li>Ensure compliance with legal obligations.</li>
        </ul>

        <hr/>

        <h2>3. How We Share Your Information</h2>
        <p>We do not sell your personal information. However, we may share your data in the following situations:</p>
        <ul>
            <li><strong>With Third-Party Services:</strong> To integrate and connect with social media platforms.</li>
            <li><strong>With Service Providers:</strong> To help us operate, maintain, and improve the Service (e.g.,
                cloud hosting, analytics providers).
            </li>
            <li><strong>For Legal Reasons:</strong> To comply with applicable laws, regulations, or legal requests.</li>
        </ul>

        <hr/>

        <h2>4. Your Choices and Rights</h2>
        <ul>
            <li><strong>Access and Update:</strong> You can view and update your account information in the app.</li>
            <li><strong>Data Deletion:</strong> You can delete your account and associated data by contacting us at <a
                href="mailto:admin@timelyturtle.com">admin@timelyturtle.com</a>.
            </li>
            <li><strong>Withdraw Consent:</strong> You can unlink your social media accounts at any time through the app
                settings.
            </li>
        </ul>

        <hr/>

        <h2>5. Data Retention</h2>
        <p>We retain your information for as long as your account is active or as needed to provide the Service. Upon
            account deletion, we will delete your data, except as required for legal or regulatory obligations.</p>

        <hr/>

        <h2>6. Security</h2>
        <p>We implement reasonable measures to protect your information from unauthorized access, disclosure,
            alteration, and destruction. However, no security system is foolproof, and we cannot guarantee the absolute
            security of your data.</p>

        <hr/>

        <h2>7. Third-Party Links</h2>
        <p>The Service may contain links to third-party websites or services. We are not responsible for the privacy
            practices of these third parties. Please review their privacy policies before providing any information.</p>

        <hr/>

        <h2>8. Children’s Privacy</h2>
        <p>TimelyTurtle is not intended for individuals under the age of 13. We do not knowingly collect personal
            information from children under 13. If we become aware of such information, we will delete it promptly.</p>

        <hr/>

        <h2>9. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or
            through the app. Continued use of the Service constitutes acceptance of the revised Privacy Policy.</p>

        <hr/>

        <h2>10. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <p>Email: <a href="mailto:admin@timelyturtle.com">admin@timelyturtle.com</a></p>

        <hr/>

        <p>Thank you for trusting TimelyTurtle to manage your social media scheduling needs!</p>

    </div>
}

export function TermsOfConditions(){
    useDocumentTitle("Terms and Conditions")
    return <div className={"container mx-auto mt-16 prose"} style={{maxWidth:600}}>
        <BackButton />
    <h1>Terms and Conditions</h1>

        <p><strong>Effective Date:</strong> 28 Dec 2024</p>

        <p>Welcome to TimelyTurtle ("we," "our," or "us"). By accessing or using our social media post scheduling app
            (the "Service"), you agree to comply with and be bound by these Terms and Conditions. Please read them
            carefully before using the Service. If you do not agree with these terms, you must not use the Service.</p>

        <hr/>

        <h2>1. Acceptance of Terms</h2>
        <p>By creating an account or otherwise using the Service, you confirm that you are at least 18 years old and
            capable of entering into a legally binding agreement. If you are using the Service on behalf of an
            organization, you agree to these terms on behalf of that organization.</p>

        <hr/>

        <h2>2. Account Registration and Security</h2>
        <ul>
            <li>You must provide accurate and complete information when registering for an account.</li>
            <li>You are responsible for maintaining the confidentiality of your login credentials and all activities
                that occur under your account.
            </li>
            <li>Notify us immediately at <a href="mailto:admin@timelyturtle.com">admin@timelyturtle.com</a> if you
                suspect unauthorized access to your account.
            </li>
        </ul>

        <hr/>

        <h2>3. Use of the Service</h2>
        <p>You agree to:</p>
        <ul>
            <li>Use the Service only for lawful purposes and in accordance with these Terms.</li>
            <li>Not use the Service to post or share content that is illegal, offensive, or violates the rights of
                others.
            </li>
            <li>Comply with all applicable laws, regulations, and third-party platform policies when using the
                Service.
            </li>
        </ul>
        <p>We reserve the right to suspend or terminate your account if you violate these Terms.</p>

        <hr/>

        <h2>4. Intellectual Property</h2>
        <p>All content, features, and functionality of the Service, including but not limited to text, graphics, logos,
            and software, are owned by TimelyTurtle or our licensors and are protected by intellectual property laws.
            You may not copy, modify, distribute, or create derivative works from the Service without our prior written
            consent.</p>

        <hr/>

        <h2>5. Subscription and Payment</h2>
        <ul>
            <li>The Service may offer free and paid subscription plans. Details of these plans are provided at the time
                of sign-up.
            </li>
            <li>By subscribing to a paid plan, you agree to pay all applicable fees.</li>
            <li>Subscription fees are non-refundable, except as required by law or explicitly stated in these Terms.
            </li>
        </ul>

        <hr/>

        <h2>6. Third-Party Services</h2>
        <p>The Service may integrate with third-party platforms such as social media networks. We are not responsible
            for the actions or policies of these third parties. Your use of third-party services is governed by their
            respective terms and conditions.</p>

        <hr/>

        <h2>7. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law:</p>
        <ul>
            <li>TimelyTurtle is not liable for any indirect, incidental, or consequential damages arising from your use
                of the Service.
            </li>
            <li>Our total liability for any claims related to the Service is limited to the amount you paid us in the 12
                months preceding the claim.
            </li>
        </ul>

        <hr/>

        <h2>8. Indemnification</h2>
        <p>You agree to indemnify and hold TimelyTurtle harmless from any claims, damages, or expenses arising from your
            use of the Service or violation of these Terms.</p>

        <hr/>

        <h2>9. Termination</h2>
        <p>We may suspend or terminate your access to the Service at any time, with or without notice, for any reason,
            including if you violate these Terms. Upon termination, your right to use the Service ceases
            immediately.</p>

        <hr/>

        <h2>10. Changes to Terms</h2>
        <p>We may update these Terms from time to time. We will notify you of significant changes by email or through
            the app. Continued use of the Service constitutes acceptance of the revised Terms.</p>

        <hr/>

        <h2>11. Governing Law</h2>
        <p>These Terms are governed by and construed in accordance with the laws of [Insert Jurisdiction]. Any disputes
            will be resolved in the courts of [Insert Jurisdiction].</p>

        <hr/>

        <h2>12. Contact Us</h2>
        <p>If you have any questions or concerns about these Terms, please contact us:</p>
        <p>Email: <a href="mailto:admin@timelyturtle.com">admin@timelyturtle.com</a></p>

        <hr/>

        <p>Thank you for choosing TimelyTurtle. We’re excited to help you streamline your social media management!</p>

    </div>
}
